import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../context/AuthContext';
import { usersAPI } from '../api';
import { Lock, Eye, EyeSlash as EyeOff, Warning as AlertTriangle, CheckCircle, ArrowsClockwise as RefreshCw } from '@phosphor-icons/react';

const ForcePasswordChangeModal = () => {
  const { user, refreshUser } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Don't show if user doesn't need to change password
  if (!user || !user.forcePasswordChange) {
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!currentPassword) {
      setError('Please enter your temporary password');
      return;
    }

    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (currentPassword === newPassword) {
      setError('New password must be different from current password');
      return;
    }

    setLoading(true);
    try {
      await usersAPI.updateProfile({
        currentPassword,
        newPassword
      });
      setSuccess(true);
      // Refresh user to clear forcePasswordChange flag
      setTimeout(async () => {
        await refreshUser();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-primaryB-900/70 flex items-center justify-center z-[100] p-4">
      <div className="bg-primaryB-50 rounded-3xl max-w-md w-full shadow-elevated dark:shadow-elevated-dark">
        <div className="p-6 border-b border-primaryB-200/60">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
              <AlertTriangle weight="bold" className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-primaryB-900">Password Change Required</h2>
              <p className="text-sm text-primaryB-500">Please set a new password to continue</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {success ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle weight="bold" className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-lg font-bold text-primaryB-900 mb-2">Password Changed!</h3>
              <p className="text-sm text-primaryB-500">Redirecting you now...</p>
            </div>
          ) : (
            <>
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-sm text-amber-700">
                  Your password was reset by an administrator. Please enter the temporary password you received and choose a new password.
                </p>
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                  {error}
                </div>
              )}

              {/* Current/Temporary Password */}
              <div>
                <label className="block text-sm font-semibold text-primaryB-700 mb-1.5">
                  Temporary Password
                </label>
                <div className="relative">
                  <Lock weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primaryB-400" />
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter temporary password"
                    className="w-full pl-10 pr-12 py-3 border border-primaryB-200/60 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-primaryB-400 hover:text-primaryB-600"
                  >
                    {showCurrentPassword ? <EyeOff weight="bold" className="w-5 h-5" /> : <Eye weight="bold" className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div>
                <label className="block text-sm font-semibold text-primaryB-700 mb-1.5">
                  New Password
                </label>
                <div className="relative">
                  <Lock weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primaryB-400" />
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Choose a new password"
                    className="w-full pl-10 pr-12 py-3 border border-primaryB-200/60 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-primaryB-400 hover:text-primaryB-600"
                  >
                    {showNewPassword ? <EyeOff weight="bold" className="w-5 h-5" /> : <Eye weight="bold" className="w-5 h-5" />}
                  </button>
                </div>
                <p className={`mt-1 text-xs ${newPassword.length >= 6 ? 'text-emerald-600' : 'text-primaryB-500'}`}>
                  {newPassword.length >= 6 ? '✓ Password is valid' : `At least 6 characters (${newPassword.length}/6)`}
                </p>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-semibold text-primaryB-700 mb-1.5">
                  Confirm New Password
                </label>
                <div className="relative">
                  <Lock weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primaryB-400" />
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your new password"
                    className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none ${
                      confirmPassword && confirmPassword === newPassword ? 'border-emerald-500' : 'border-primaryB-200/60'
                    }`}
                  />
                </div>
                {confirmPassword && (
                  <p className={`mt-1 text-xs ${confirmPassword === newPassword ? 'text-emerald-600' : 'text-red-500'}`}>
                    {confirmPassword === newPassword ? '✓ Passwords match' : 'Passwords do not match'}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading || newPassword.length < 6 || newPassword !== confirmPassword}
                className="w-full py-3 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all duration-200"
              >
                {loading ? (
                  <>
                    <RefreshCw weight="bold" className="w-5 h-5 animate-spin" />
                    Changing Password...
                  </>
                ) : (
                  'Change Password'
                )}
              </button>
            </>
          )}
        </form>
      </div>
    </div>
  , document.body);
};

export default ForcePasswordChangeModal;
